import { Box, Breadcrumbs, Button, debounce, InputBase, LinearProgress, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search';
import CreateProjectDialog from '../../components/CreateProjectDialog';
import { format } from 'date-fns';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DataGridPremium, GridActionsCellItem, GridToolbarContainer, GridToolbarQuickFilter, useGridApiRef } from '@mui/x-data-grid-premium';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import EditProjectDialog from '../../components/EditProjectDialog';
import ConfirmDialogReuse from './ReuseComponents/ConfirmDialogReuse';
import ResponseDialog from './ReuseComponents/ConfirmDialogReuse';
import HomeIcon from '@mui/icons-material/Home';
import { LoadingButton } from '@mui/lab';
import { CustomTooltip } from '../../components/TooltipCustomStyle';
import TooltipStatusTitle from '../../components/TooltipStatusTitle';
import ErrorIcon from '@mui/icons-material/Error';
import checkUpdateSubjectSchema from '../../utils/checkUpdateSubjectSchema';
import { convertOnePolicyStructure } from '../../utils/convertPolicyStructure';
import { setSnackbarMessage } from '../../redux/snackbarMessageSlice';
import { getProfile } from '../../redux/iaamSlice';

let hasVerifiedProjectsOnce = false;
let cachedProjects = [];

function ProjectPage() {
    const theme = useTheme();

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [open, setOpen] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState(false);
    const [projects, setProjects] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [projectSelected, setProjectSelected] = useState();
    const [openResponse, setOpenResponse] = useState(false);
    const [responseStatus, setResponseStatus] = useState("");

    const [isLoading, setIsLoading] = useState(false);
    const [loading, setLoading] = useState(false);

    // const [profile, setProfile] = useState(null);

    const profile = useSelector(getProfile);

    const updateProjectsState = (nextProjects) => {
        cachedProjects = nextProjects;
        setProjects(nextProjects);
    };

    useEffect(() => {
        if (profile) {

            const creatorId = profile?.inum;

            if (cachedProjects.length > 0) {
                setProjects(cachedProjects);
                return;
            }

            if (!hasVerifiedProjectsOnce) {
                verifyProjects(creatorId);
                return;
            }

            getListProjects(creatorId);
        }
    }, [profile]);

    const apiRef = useGridApiRef();

    const columns = [
        {
            field: 'project_name',
            headerName: 'Project Name',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => {
                const onClick = () => {
                    handleProjectSelected(params.row);
                };

                const item = params.row;
                const filterSummary = item?.validation?.map(v => v.summary) || [];

                return (
                    <>
                        {filterSummary.length > 0 && (
                            <CustomTooltip
                                title={
                                    <TooltipStatusTitle
                                        headerTitle="Error"
                                        listDetails={filterSummary}
                                    />
                                }
                                arrow
                                placement="top"
                                tooltipcolorstyle={theme.palette.error.main}
                            >
                                <ErrorIcon
                                    sx={{
                                        color: theme.palette.error.main,
                                        fontSize: 16,
                                        marginRight: 0.5
                                    }}
                                />
                            </CustomTooltip>
                        )}
                        <a
                            href="#"
                            className="text-blue-900 font-primary"
                            onClick={onClick}
                            style={{
                                display: 'inline-flex', // Ensure the link stays inline with the icon
                                alignItems: 'center', // Align text vertically with the icon
                            }}
                        >
                            {item.project_name}
                        </a>
                    </>
                );
            }
        },
        {
            field: 'creator_name',
            headerName: 'Create by',
            width: 220
        },
        {
            field: 'update_timestamp',
            headerName: 'Date Modified',
            width: 200
        },
        {
            field: 'actions',
            type: 'actions',
            width: 60,
            getActions: (params) => [
                <GridActionsCellItem
                    key={`action-${params.id}`}
                    icon={<MoreVertIcon />}
                    label="Action"
                    onClick={(e) => handleActionClick(params.row, e)}
                />,
            ],
        },
    ];

    const handleGetProject = async (row) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/project/get-project/${row.project_id}`);
        if (response.ok) {
            const result = await response.json();
            // console.log("result", result)
            localStorage.setItem('project', JSON.stringify(result.project));
            navigate('/editor/workspace');
        } else {
            console.error(response);
        }
    };

    const handleProjectSelected = async (row) => {
        handleClearLocalStorage();
        handleGetProject(row);
    }

    const handleClearLocalStorage = () => {
        const keysToRemove = ['dpe-config', 'project', 'subject-diagram', 'subject-file', 'object-diagram', 'object-file', 'regulatory-diagram', 'regulatory-file'];
        keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
        });
    };

    const handleActionClick = (row, e) => {
        setProjectSelected(row)
        setAnchorEl(e.currentTarget);
    }

    const handleEditClick = () => {
        setAnchorEl(null);
        setOpenEdit(true);
    };

    const handleDeleteClick = () => {
        setAnchorEl(null);
        setOpenDelete(true);
    };

    const handleOpenCreateProject = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onGetProjects = async (creatorId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/project/get-projects?creator_id=${creatorId}`);
            // const response = await fetch(`${process.env.REACT_APP_API_URL}/project/get-projects`);
            if (!response.ok) {
                throw new Error(`Failed to fetch projects. Please try again later.`);
            }
            return response;
        } catch (error) {
            return error;
        }
    };

    const verifyProjects = async (creatorId) => {
        if (!creatorId) return;
        setIsLoading(true)
        try {
            const response1 = await onGetProjects(creatorId);
            if (!response1.ok) {
                throw new Error(response1.error.message || `Failed to fetch projects. Please try again later.`);
            }
            const result1 = await response1.json();
            const projectIds = result1.projects.map(project => {
                return project.project_id;
            });

            const requestOptions = {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(projectIds)
            };

            const responseVerify = await fetch(`${process.env.REACT_APP_API_VALIDATION_URL}/verify/projects`, requestOptions);
            if (responseVerify.ok) {
                const response2 = await onGetProjects(creatorId);
                if (!response2.ok) {
                    throw new Error(`Failed to fetch projects after verification. Please try again later.`);
                }
                const result2 = await response2.json();
                const updatedItems = result2.projects.map((item, index) => ({
                    ...item,
                    id: index,
                    update_timestamp: format(new Date(item.update_timestamp), "yyyy-MM-dd HH:mm:ss"),
                }));
                const isSubjectSchema = await checkUpdateSubjectSchema(dispatch);
                if (!isSubjectSchema) {
                    const updateValidation = updatedItems.map(project => {
                        return {
                            ...project,
                            validation: [...project.validation, {
                                code: 'SUBJECT_SCHEMA_FETCH_FAILED',
                                summary: 'Subject schema fetch failed. Please try again.',
                                detail: []
                            }]
                        }
                    })
                    updateProjectsState(updateValidation);
                } else {
                    updateProjectsState(updatedItems);
                }
                hasVerifiedProjectsOnce = true;
                setIsLoading(false)
            }
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message,
                status: "error",
            }));
        }
    };

    const getListProjects = async (creatorId) => {
        if (!creatorId) return;
        setIsLoading(true)
        try {
            const response = await onGetProjects(creatorId);
            if (!response.ok) {
                throw new Error(response.error.message || `Failed to fetch projects. Please try again later.`);
            }
            const result = await response.json();
            const updatedItems = result.projects.map((item, index) => ({
                ...item,
                id: index,
                update_timestamp: format(new Date(item.update_timestamp), "yyyy-MM-dd HH:mm:ss"),
            }));
            const isSubjectSchema = await checkUpdateSubjectSchema(dispatch);
            if (!isSubjectSchema) {
                const updateValidation = updatedItems.map(project => {
                    return {
                        ...project,
                        validation: [...project.validation, {
                            code: 'SUBJECT_SCHEMA_FETCH_FAILED',
                            summary: 'Subject schema fetch failed. Please try again.',
                            detail: []
                        }]
                    }
                })
                updateProjectsState(updateValidation);
            } else {
                updateProjectsState(updatedItems);
            }
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            dispatch(setSnackbarMessage({
                open: true,
                message: error.message,
                status: "error",
            }));
        }
    }

    const handleDelete = async () => {
        setLoading(true);

        const requestOptions = {
            method: "DELETE"
        };

        const response = await fetch(process.env.REACT_APP_API_URL + `/project/delete-project/${projectSelected.project_id}`, requestOptions);
        if (response.status === 200) {
            localStorage.removeItem('project');
            setProjectSelected(null);
            setStatusUpdate(true);
            setLoading(false);
            onDeleteClose();
            // setResponseStatus("The project and their associated diagrams have been deleted.");
            // setOpenResponse(true);
        } else {
            console.error(response.status);
            setLoading(false);
            setResponseStatus(`Error status: ${response.status}`);
            setOpenResponse(true);
        }

    };

    useEffect(() => {
        if (statusUpdate) {
            getListProjects(profile?.inum);
            setStatusUpdate(false);
        }
    }, [statusUpdate, profile])

    const updateSearchValue = React.useMemo(() => {
        return debounce((newValue) => {
            apiRef.current.setQuickFilterValues(
                newValue.split(" ").filter((word) => word !== "")
            );
        }, 500);
    }, [apiRef]);

    const onDeleteClose = () => {
        setOpenDelete(false);
    };

    const onResponseClose = () => {
        setOpenResponse(false);
    };

    function CustomToolbar(props) {

        return (
            <GridToolbarContainer {...props} className="mt-2 ml-2">
                <GridToolbarQuickFilter variant="outlined" size="small" />

            </GridToolbarContainer>
        );
    }

    return (
        <section className="overflow-auto w-full bg-gradient-to-r from-sky-50 via-blue-50 to-blue-100">
            <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 overflow-hidden w-full '>
                <div className='pt-10'>
                    <div role='presentation'>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Typography sx={{ color: 'text.primary', display: 'flex', alignItems: 'center' }} variant='h6'>
                                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />Policy Editor
                            </Typography>
                        </Breadcrumbs>
                    </div>
                </div>
                <div className='animate-fade-right'>
                    <div className='grid gap-1 mt-8'>
                        <div className='flex justify-between bg-white rounded-t-md h-24 p-4 border border-gray-200'>
                            <div className='h-full content-evenly'>
                                <Typography variant='h6'>Policy Project</Typography>
                                <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>Create new & Manage policy project</Typography>
                            </div>
                            <div className='h-full content-evenly'>
                                <Button style={{ textTransform: 'none' }} variant="contained" onClick={handleOpenCreateProject}>
                                    New Project
                                </Button>
                            </div>
                        </div>
                        <div className='bg-white rounded-b-md h-[calc(100vh-270px)] border border-gray-200'>
                            <DataGridPremium
                                apiRef={apiRef}
                                loading={isLoading}
                                rows={projects}
                                columns={columns}
                                disableRowSelectionOnClick
                                disableDensitySelector
                                slots={{ loadingOverlay: LinearProgress, toolbar: CustomToolbar }}
                                sx={{
                                    border: 0
                                }}
                            />
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem onClick={handleEditClick}>Edit</MenuItem>
                                <MenuItem onClick={handleDeleteClick}>Delete</MenuItem>
                            </Menu>
                        </div>
                    </div>
                    <CreateProjectDialog open={open} setOpen={setOpen} setStatusUpdate={setStatusUpdate} />
                    <EditProjectDialog open={openEdit} setOpen={setOpenEdit} setStatusUpdate={setStatusUpdate} projectEdit={projectSelected} />
                    <ConfirmDialogReuse
                        open={openDelete}
                        maxWidth={"sm"}
                        content={
                            <div>
                                <Typography variant="subtitle1">Are you sure you want to delete project "{projectSelected?.project_name}" ?</Typography>
                            </div>
                        }
                        actions={
                            <div className='grid grid-cols-2 gap-2'>
                                <LoadingButton
                                    variant="contained"
                                    loading={loading}
                                    onClick={() => handleDelete()}
                                >
                                    Confirm
                                </LoadingButton>
                                <Button
                                    disabled={loading}
                                    variant="outlined"
                                    onClick={() => onDeleteClose()}
                                >
                                    Cancel
                                </Button>
                            </div>
                        }
                    />
                    <ResponseDialog
                        open={openResponse}
                        maxWidth={"sm"}
                        content={
                            <div>
                                <Typography variant="subtitle1">{responseStatus}</Typography>
                            </div>
                        }
                        actions={
                            <Button
                                variant="outlined"
                                onClick={() => onResponseClose()}
                            >
                                Close
                            </Button>
                        }
                    />
                </div>
            </div>

        </section>
    )
}

export default ProjectPage